/* =========================================================
   Mattress Home — Cart Cross-sell V2.5
   Route: assets/mh-cart-sleep-setup.js

   Add to Cart fix:
   - Uses delegated capture-phase click handling.
   - Stops theme/Turbo handlers from swallowing the cross-sell click.
   - Uses Shopify-compatible FormData payload: id + quantity.
   - Verifies cart/add.js success.
   - Falls back to a real POST form outside #cart_form if AJAX fails.
   - Works even if the cross-sell DOM is re-rendered after page load.
   ========================================================= */

(function () {
  'use strict';

  window.MHCartCrossSellVersion = '2.5.0';

  function rootUrl() {
    var root = '/';

    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      root = window.Shopify.routes.root;
    }

    if (root.charAt(root.length - 1) !== '/') {
      root += '/';
    }

    return root;
  }

  function cartUrl(path) {
    return rootUrl() + path.replace(/^\/+/, '');
  }

  function moneyUSD(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format((Number(cents) || 0) / 100);
  }

  function shortMoney(cents) {
    return moneyUSD(cents).replace('.00', '');
  }

  function setFeedback(card, message, isError) {
    if (!card) return;

    var feedback = card.querySelector('[data-mh-cart-feedback]');
    if (!feedback) return;

    feedback.hidden = !message;
    feedback.textContent = message || '';
    feedback.classList.toggle('is-error', !!isError);
    feedback.classList.toggle('is-success', !!message && !isError);
  }

  function getSelectedVariantId(card, button) {
    if (!card || !button) return 0;

    var select = card.querySelector('[data-mh-variant-select]');
    var raw = select && select.value ? select.value : button.getAttribute('data-variant-id');

    return parseInt(raw, 10) || 0;
  }

  function updateSavings(card, price, compare) {
    if (!card) return;

    var badge = card.querySelector('[data-mh-save-badge]');
    var priceEl = card.querySelector('[data-mh-current-price]');
    var compareEl = card.querySelector('[data-mh-compare-price]');
    var savingsEl = card.querySelector('[data-mh-savings-text]');

    if (priceEl) {
      priceEl.textContent = moneyUSD(price);
    }

    if (compareEl) {
      if (compare > price) {
        compareEl.hidden = false;
        compareEl.textContent = moneyUSD(compare);
      } else {
        compareEl.hidden = true;
        compareEl.textContent = '';
      }
    }

    var savings = compare > price ? compare - price : 0;
    var pct = compare > price ? Math.floor((savings * 100) / compare) : 0;
    var badgeText = pct >= 50 ? pct + '% OFF' : 'SAVE ' + shortMoney(savings);
    var inlineText = pct >= 50 ? pct + '% OFF' : 'You save ' + shortMoney(savings);

    if (savings > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'mh-cart-product-card__save-badge';
        badge.setAttribute('data-mh-save-badge', '');

        var wrap = card.querySelector('.mh-cart-product-card__media-wrap');
        if (wrap) {
          wrap.appendChild(badge);
        }
      }

      badge.hidden = false;
      badge.textContent = badgeText;

      if (savingsEl) {
        savingsEl.hidden = false;
        savingsEl.textContent = inlineText;
      }
    } else {
      if (badge) {
        badge.hidden = true;
      }

      if (savingsEl) {
        savingsEl.hidden = true;
        savingsEl.textContent = '';
      }
    }
  }

  function postAddAjax(variantId) {
    var data = new FormData();
    data.append('id', String(variantId));
    data.append('quantity', '1');

    return fetch(cartUrl('cart/add.js'), {
      method: 'POST',
      body: data,
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json'
      }
    }).then(function (response) {
      return response.json()
        .catch(function () {
          return {};
        })
        .then(function (payload) {
          if (!response.ok) {
            var msg =
              payload.description ||
              payload.message ||
              'Shopify could not add this option.';
            throw new Error(msg);
          }

          return payload;
        });
    });
  }

  function verifyVariantInCart(variantId) {
    return fetch(cartUrl('cart.js'), {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json'
      },
      cache: 'no-store'
    })
      .then(function (response) {
        if (!response.ok) return false;
        return response.json();
      })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return false;

        return cart.items.some(function (item) {
          return Number(item.variant_id || item.id) === Number(variantId);
        });
      })
      .catch(function () {
        /* Verification failure should not undo a successful cart/add.js. */
        return true;
      });
  }

  function fallbackPost(variantId) {
    /* Important: the cross-sell sits inside #cart_form, so never place this
       fallback form inside that form. Append it directly to document.body. */
    var form = document.createElement('form');

    form.method = 'POST';
    form.action = cartUrl('cart/add');
    form.style.display = 'none';

    var idInput = document.createElement('input');
    idInput.type = 'hidden';
    idInput.name = 'id';
    idInput.value = String(variantId);

    var qtyInput = document.createElement('input');
    qtyInput.type = 'hidden';
    qtyInput.name = 'quantity';
    qtyInput.value = '1';

    var returnInput = document.createElement('input');
    returnInput.type = 'hidden';
    returnInput.name = 'return_to';
    returnInput.value = cartUrl('cart');

    form.appendChild(idInput);
    form.appendChild(qtyInput);
    form.appendChild(returnInput);

    document.body.appendChild(form);
    form.submit();
  }

  function addProduct(button) {
    if (!button || button.getAttribute('aria-busy') === 'true') return;

    var card = button.closest('[data-mh-product-card]');
    var variantId = getSelectedVariantId(card, button);

    if (!variantId) {
      setFeedback(card, 'Please select an available option.', true);
      return;
    }

    var originalText = button.textContent;

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    button.classList.add('is-loading');
    button.textContent = 'Adding...';

    setFeedback(card, '', false);

    postAddAjax(variantId)
      .then(function () {
        return verifyVariantInCart(variantId);
      })
      .then(function (isPresent) {
        if (!isPresent) {
          throw new Error('The item was not confirmed in the cart.');
        }

        button.classList.remove('is-loading');
        button.classList.add('is-added');
        button.textContent = 'Added ✓';
        setFeedback(card, 'Added to your cart.', false);

        window.setTimeout(function () {
          window.location.assign(cartUrl('cart'));
        }, 350);
      })
      .catch(function (error) {
        setFeedback(
          card,
          error && error.message
            ? error.message + ' Retrying...'
            : 'Unable to add this option. Retrying...',
          true
        );

        /* The normal form POST is Shopify's most compatible fallback. */
        window.setTimeout(function () {
          fallbackPost(variantId);
        }, 150);

        /* If submission is blocked for any reason, restore the button. */
        window.setTimeout(function () {
          if (document.visibilityState === 'visible') {
            button.removeAttribute('aria-busy');
            button.disabled = false;
            button.classList.remove('is-loading');
            button.textContent = originalText;
          }
        }, 2500);
      });
  }

  function updateRailState(shell) {
    var track = shell.querySelector('[data-mh-rail-track]');
    var prev = shell.querySelector('[data-mh-rail-prev]');
    var next = shell.querySelector('[data-mh-rail-next]');

    if (!track || !prev || !next) return;

    var hasOverflow = track.scrollWidth > track.clientWidth + 4;

    if (!hasOverflow) {
      prev.disabled = true;
      next.disabled = true;
      return;
    }

    var maxScroll = Math.max(0, track.scrollWidth - track.clientWidth - 2);

    prev.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft >= maxScroll;
  }

  function scrollRail(button, direction) {
    var shell = button.closest('[data-mh-product-rail]');
    if (!shell) return;

    var track = shell.querySelector('[data-mh-rail-track]');
    if (!track) return;

    var amount = Math.max(180, Math.floor(track.clientWidth * 0.75));

    track.scrollBy({
      left: direction * amount,
      behavior: 'smooth'
    });
  }

  /* One delegated click listener in CAPTURE phase.
     This prevents Florence/Turbo handlers from swallowing the button click. */
  document.addEventListener('click', function (event) {
    var addButton = event.target.closest('[data-mh-add-setup-product]');

    if (addButton) {
      event.preventDefault();
      event.stopPropagation();

      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      addProduct(addButton);
      return;
    }

    var prev = event.target.closest('[data-mh-rail-prev]');
    if (prev) {
      event.preventDefault();
      scrollRail(prev, -1);
      return;
    }

    var next = event.target.closest('[data-mh-rail-next]');
    if (next) {
      event.preventDefault();
      scrollRail(next, 1);
    }
  }, true);

  /* Variant changes are also delegated, so dynamic section reloads work. */
  document.addEventListener('change', function (event) {
    var select = event.target.closest('[data-mh-variant-select]');
    if (!select) return;

    var card = select.closest('[data-mh-product-card]');
    if (!card) return;

    var option = select.options[select.selectedIndex];
    var button = card.querySelector('[data-mh-add-setup-product]');

    if (button) {
      button.setAttribute('data-variant-id', select.value);
    }

    if (option) {
      updateSavings(
        card,
        Number(option.getAttribute('data-price')) || 0,
        Number(option.getAttribute('data-compare')) || 0
      );
    }

    setFeedback(card, '', false);
  }, true);

  function refreshRails(root) {
    (root || document).querySelectorAll('[data-mh-product-rail]').forEach(function (shell) {
      window.requestAnimationFrame(function () {
        updateRailState(shell);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      refreshRails(document);
    });
  } else {
    refreshRails(document);
  }

  window.addEventListener('resize', function () {
    refreshRails(document);
  });

  document.addEventListener('scroll', function (event) {
    var track = event.target.closest && event.target.closest('[data-mh-rail-track]');
    if (!track) return;

    var shell = track.closest('[data-mh-product-rail]');
    if (shell) {
      window.requestAnimationFrame(function () {
        updateRailState(shell);
      });
    }
  }, true);

  document.addEventListener('shopify:section:load', function (event) {
    refreshRails(event.target);
  });
})();
